import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
    Book,
    Bullhorn,
    Calendar,
    CalendarSettings,
    Document,
    Help,
    Money,
    PedestrianFamily,
    Settings,
    ShoppingCart,
    UserMultiple,
} from '@carbon/icons-react';

import styles from './Container.module.scss';

import LeftMenuPanel from 'Components/LeftMenuPanel/LeftMenuPanel';
import TopBar from 'Components/TopBar/TopBar';
import sharedMessages from 'Components/utils/sharedMessages';
import { MainContainerProvider } from './context/ContainerContext';

type Props = {
    children: JSX.Element;
};

const Container = ({ children }: Props) => {
    const location = useLocation();
    const intl = useIntl();

    const messages = useMemo(() => ({
        '/': {
            title: intl.formatMessage(sharedMessages.events),
            description: intl.formatMessage(sharedMessages.eventsDescription),
            icon: <Calendar />,
        },
        '/events': {
            title: intl.formatMessage(sharedMessages.events),
            description: intl.formatMessage(sharedMessages.eventsDescription),
            icon: <Calendar />,
        },
        '/announcements': {
            title: intl.formatMessage(sharedMessages.announcements),
            description: intl.formatMessage(sharedMessages.announcementsDescription),
            icon: <Bullhorn />,
        },
        '/intentions': {
            title: intl.formatMessage(sharedMessages.intentions),
            description: intl.formatMessage(sharedMessages.intentionsDescription),
            icon: <Document />,
        },
        '/team': {
            title: intl.formatMessage(sharedMessages.peopleInParish),
            description: intl.formatMessage(sharedMessages.peopleInParishDescription),
            icon: <UserMultiple />,
        },
        '/entities': {
            title: intl.formatMessage(sharedMessages.communities),
            description: intl.formatMessage(sharedMessages.communitiesDescription),
            icon: <PedestrianFamily />,
        },
        '/events-configuration': {
            title: intl.formatMessage(sharedMessages.eventsConfig),
            description: intl.formatMessage(sharedMessages.eventsConfigDescription),
            icon: <CalendarSettings />,
        },
        '/settings': {
            title: intl.formatMessage(sharedMessages.parishSettings),
            description: intl.formatMessage(sharedMessages.parishSettingsDescription),
            icon: <Settings />,
        },
        '/subscription': {
            title: intl.formatMessage(sharedMessages.subscription),
            description: intl.formatMessage(sharedMessages.subscriptionDescription),
            icon: <Money />,
        },
        '/help': {
            title: intl.formatMessage(sharedMessages.help),
            description: intl.formatMessage(sharedMessages.helpDescription),
            icon: <Help />,
        },
        '/parish-book': {
            title: intl.formatMessage(sharedMessages.parishTeam),
            description: intl.formatMessage(sharedMessages.parishTeamDescription),
            icon: <Book />,
        },
        '/offers': {
            title: intl.formatMessage(sharedMessages.offers),
            description: intl.formatMessage(sharedMessages.offersDescription),
            icon: <ShoppingCart />,
        },
    }), [intl]);
    type MessagesPath = keyof typeof messages;
    const path = location.pathname as MessagesPath;

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const currentMessages = messages[path] ?? messages['/'];

    return (
        <MainContainerProvider>
            <div className={styles.container}>
                <LeftMenuPanel />
                <div className={styles.innerContainer}>
                    <TopBar
                        title={currentMessages.title}
                        description={currentMessages.description}
                        icon={currentMessages.icon}
                    />
                    <div className={styles.elementsContainer}>
                        {children}
                    </div>
                </div>
            </div>
        </MainContainerProvider>
    );
};

export default Container;
